/**
 * Safety Vault — Dipali's one-button backup page.
 *
 * Doctrine (20-Jun-2026 · TDC pattern): admin dashboards become mazes
 * and founders get scared to click anything. This is the opposite:
 * one URL (/safety-vault), one big button, last 5 runs visible, no
 * other clickable surface area.
 *
 * Auth: ProtectedRoute + CEO role (gated in App.js).
 *
 * What you see:
 *   1. Big green button: "Run Backup Now"
 *   2. WhatsApp recipient list (who'll get pinged) — drawn live from
 *      the same resolver the backend uses, so the page never lies
 *   3. Last 5 backup runs with status + size + duration
 *   4. Status of last backup (Drive connection, retention)
 *   5. Nothing else.
 */
import React, { useEffect, useState } from "react";
import { Shield, RefreshCw, CheckCircle2, XCircle, Loader2, Phone, KeyRound, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => {
  // The HttpOnly cookie carries auth; we also bearer-token belt-and-
  // suspenders for environments where cookies are stripped (CDN edge).
  const tok = localStorage.getItem("access_token") || localStorage.getItem("token");
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

function EnvSnapshotCard() {
  const [pw, setPw] = useState("");
  const [to, setTo] = useState("dipali@mindescapes.in");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const send = async () => {
    if (pw.length < 8) { setMsg({ kind: "err", text: "Password must be at least 8 characters." }); return; }
    setBusy(true); setMsg(null);
    try {
      const res = await fetch(`${API}/admin/snapshot/env-snapshot/email`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({ password: pw, to }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "send failed");
      setMsg({ kind: "ok", text: `Encrypted zip emailed to ${data.sent_to}. Remember your password — it isn't stored anywhere.` });
      setPw("");
    } catch (e) {
      setMsg({ kind: "err", text: `Failed: ${e.message}` });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E8DFD0] bg-white p-6 mb-10" data-testid="env-snapshot-card">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound size={16} style={{ color: "#15110D" }} />
        <h2 className="text-sm font-semibold" style={{ color: "#15110D" }}>Env snapshot — secrets escape hatch</h2>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Emails a password-protected (AES-256) zip of both live .env files. Together with the Drive DB dump,
        this is everything needed to rebuild ScaleBoard anywhere. The password you type is never stored.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
          placeholder="Zip password (min 8 chars)" data-testid="env-snapshot-password"
          className="border border-[#E8DFD0] rounded-lg px-3 py-2 text-sm w-56 focus:outline-none focus:border-[#16A34A]" />
        <input type="email" value={to} onChange={(e) => setTo(e.target.value)}
          data-testid="env-snapshot-to"
          className="border border-[#E8DFD0] rounded-lg px-3 py-2 text-sm w-64 focus:outline-none focus:border-[#16A34A]" />
        <button onClick={send} disabled={busy} data-testid="env-snapshot-send"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
          style={{ background: "#15110D" }}>
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />}
          {busy ? "Encrypting & sending…" : "Email encrypted snapshot"}
        </button>
      </div>
      {msg && (
        <div className="mt-3 px-3 py-2 rounded-lg text-xs" data-testid="env-snapshot-msg"
          style={{ background: msg.kind === "ok" ? "#ECF7EF" : "#FDECEC", color: msg.kind === "ok" ? "#1B4A2E" : "#7A2B2B" }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

export default function SafetyVault() {
  const [status, setStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [triggering, setTriggering] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, j, r] = await Promise.all([
        fetch(`${API}/sitevault/status`, { headers: headers(), credentials: "include" }),
        fetch(`${API}/sitevault/jobs?limit=5`, { headers: headers(), credentials: "include" }),
        fetch(`${API}/sitevault/alert-recipients`, { headers: headers(), credentials: "include" }),
      ]);
      if (s.ok) setStatus(await s.json());
      if (j.ok) {
        const jd = await j.json();
        setJobs(jd.items || []);
      }
      if (r.ok) {
        const rd = await r.json();
        setRecipients(rd.recipients || []);
      }
    } catch (e) {
      setFeedback({ kind: "error", text: `Could not refresh: ${e.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // Live-poll every 20s — covers a backup that takes <5min, no spam.
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
  }, []);

  const runBackup = async () => {
    if (triggering) return;
    setTriggering(true);
    setFeedback(null);
    try {
      const res = await fetch(`${API}/sitevault/run-now`, {
        method: "POST",
        headers: { ...headers(), "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ scope: "full" }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setFeedback({
          kind: "ok",
          text: `Started · Job ${data.job_id || "queued"}. You'll get a WhatsApp ping when it's done (usually 3-5 min).`,
        });
        setTimeout(refresh, 4000);
      } else {
        setFeedback({ kind: "error", text: data.detail || "Backup could not be started." });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: `Network error: ${e.message}` });
    } finally {
      setTriggering(false);
    }
  };

  const runningJob = jobs.find((j) => j.status === "running");
  const lastDone = jobs.find((j) => j.status === "done");

  return (
    <div className="min-h-screen" style={{ background: "#FAF8F2" }} data-testid="safety-vault-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <Shield size={26} style={{ color: "#15110D" }} />
          <h1 className="text-2xl font-semibold" style={{ color: "#15110D" }}>
            Safety Vault
          </h1>
        </div>
        <p className="text-sm text-gray-600 mb-10">
          One button. Backs up the entire ScaleBoard database, source code, and documents to your Google Drive.
          You and Swaroop get pinged on WhatsApp when it finishes.
          {" "}<a href="/disaster-recovery" data-testid="dr-drill-link" className="font-semibold underline" style={{ color: "#B88C40" }}>
            Disaster recovery drill →
          </a>
        </p>

        {/* ── Hero button ─────────────────────────────────────────── */}
        <div className="rounded-2xl border border-[#E8DFD0] bg-white p-8 mb-10 shadow-sm">
          {runningJob ? (
            <div className="flex flex-col items-center text-center gap-3 py-4" data-testid="safety-vault-running">
              <Loader2 className="animate-spin" size={32} style={{ color: "#16A34A" }} />
              <div className="text-sm" style={{ color: "#15110D" }}>
                Backup in progress…
              </div>
              <div className="text-xs text-gray-500">
                Step: <strong>{runningJob.current_step?.name || "starting"}</strong>
                {" · "}Started {fmtAgo(runningJob.started_at)}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center text-center gap-4">
              <button
                onClick={runBackup}
                disabled={triggering}
                data-testid="safety-vault-run-button"
                className="px-10 py-5 rounded-2xl text-white text-lg font-semibold transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                style={{ background: "#16A34A" }}
              >
                {triggering ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="animate-spin" size={20} /> Starting…</span>
                ) : (
                  "Run Backup Now"
                )}
              </button>
              {lastDone && (
                <div className="text-xs text-gray-500">
                  Last successful: {fmtAgo(lastDone.finished_at || lastDone.started_at)} · {lastDone.uploaded_count || 0} files · {lastDone.total_uploaded_mb?.toFixed(1) || "0"} MB
                </div>
              )}
            </div>
          )}

          {feedback && (
            <div
              data-testid="safety-vault-feedback"
              className="mt-6 px-4 py-3 rounded-lg text-sm"
              style={{
                background: feedback.kind === "ok" ? "#ECF7EF" : "#FDECEC",
                color: feedback.kind === "ok" ? "#1B4A2E" : "#7A2B2B",
                borderLeft: `3px solid ${feedback.kind === "ok" ? "#16A34A" : "#DC2626"}`,
              }}
            >
              {feedback.text}
            </div>
          )}
        </div>

        {/* ── Env snapshot — secrets escape hatch ─────────────────── */}
        <EnvSnapshotCard />

        {/* ── Who gets pinged ─────────────────────────────────────── */}
        <div className="rounded-2xl border border-[#E8DFD0] bg-white p-6 mb-10">
          <div className="flex items-center gap-2 mb-3">
            <Phone size={16} style={{ color: "#15110D" }} />
            <h2 className="text-sm font-semibold" style={{ color: "#15110D" }}>WhatsApp recipients on every backup</h2>
          </div>
          {recipients.length === 0 ? (
            <div className="text-xs text-gray-500">{loading ? "Loading…" : "No recipients configured."}</div>
          ) : (
            <div className="space-y-1.5">
              {recipients.map((r) => (
                <div key={r.phone} className="text-sm flex items-center gap-2" data-testid={`recipient-${r.phone}`}>
                  <span style={{ color: "#15110D" }} className="font-medium">{r.name}</span>
                  <span className="text-gray-500">· +{r.phone}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Last 5 runs ─────────────────────────────────────────── */}
        <div className="rounded-2xl border border-[#E8DFD0] bg-white p-6 mb-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold" style={{ color: "#15110D" }}>Recent backups</h2>
            <button onClick={refresh} disabled={loading} className="text-xs text-gray-500 hover:text-[#15110D]" data-testid="safety-vault-refresh">
              <RefreshCw size={12} className={`inline ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
          {jobs.length === 0 ? (
            <div className="text-xs text-gray-500">No backup runs yet.</div>
          ) : (
            <div className="space-y-2">
              {jobs.slice(0, 5).map((j) => (
                <div key={j._id} className="flex items-center gap-3 text-sm py-2 border-b last:border-b-0 border-gray-100" data-testid={`safety-vault-job-${j.status}`}>
                  {j.status === "done" ? (
                    <CheckCircle2 size={16} style={{ color: "#16A34A" }} />
                  ) : j.status === "failed" ? (
                    <XCircle size={16} style={{ color: "#DC2626" }} />
                  ) : (
                    <Loader2 className="animate-spin" size={16} style={{ color: "#2563EB" }} />
                  )}
                  <div className="flex-1">
                    <div style={{ color: "#15110D" }}>
                      {fmtTime(j.started_at)} · <span className="text-gray-500">{j.scope}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {(j.uploaded_count || 0)} files · {(j.total_uploaded_mb || 0).toFixed(1)} MB · {(j.duration_seconds || 0).toFixed(0)}s
                      {j.crash_error && <span style={{ color: "#7A2B2B" }} className="ml-2">· {j.crash_error.substring(0, 60)}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Drive status (collapsed) ────────────────────────────── */}
        {status?.drive && (
          <div className="text-xs text-gray-500 text-center">
            Drive: {status.drive.ok ? "✓ connected" : "✗ disconnected"} ·{" "}
            Daily snapshots retained: {status.retention?.daily_db || 30}d ·{" "}
            Weekly archives: {status.retention?.weekly_gold || 12}w
            <br />
            <Link to="/dashboard" className="hover:underline text-gray-400">← Back to dashboard</Link>
          </div>
        )}
      </div>
    </div>
  );
}

function fmtAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)} min ago`;
  if (ms < 86400_000) return `${Math.floor(ms / 3600_000)} h ago`;
  return `${Math.floor(ms / 86400_000)} d ago`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}
